import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import AddNewButton from '../../../components/common/AddButton';
import Drawer from '../../../components/common/Drawer';
import FormSection from '../../../components/common/FormSection';
import MemberFeeForm from '../../../components/forms/MemberFeeForm';
import MemberTypeForm from '../../../components/forms/MemberTypeForm';
import AdminLayout from '../../../components/layout/AdminLayout';
import MemberFeeTable from '../../../components/tables/MemberFeeTable';
import { API_BASE_URL } from '../../../config/config';
import {
  useMemberTypeById,
  useUpdateMemberType,
} from '../../../hooks/useMemberTypeApi';
import { MemberFeeClient, MemberFeeDTO } from '../../../services/apiClient';
import axiosInstance from '../../../services/axiosInstance';
import { confirmDelete } from '../../../utils/sweetAlertUtils';

const memberFeeClient = new MemberFeeClient(API_BASE_URL, axiosInstance);

type DrawerView = 'add-fee' | 'edit-fee' | null;

const MemberTypeEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: entityData } = useMemberTypeById(id ?? '');
  const updateMemberType = useUpdateMemberType();
  const navigate = useNavigate();

  const [fees, setFees] = useState<MemberFeeDTO[]>([]);
  const [isFeesLoading, setIsFeesLoading] = useState(false);
  const [isDrawerSaving, setIsDrawerSaving] = useState(false);

  const [drawerView, setDrawerView] = useState<DrawerView>(null);
  const [selectedFee, setSelectedFee] = useState<MemberFeeDTO | null>(null);

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Member Types', href: '/admin/membertypes' },
    { label: 'Edit', href: '#' },
  ];

  const fetchFees = useCallback(async () => {
    if (!id) {
      return;
    }
    setIsFeesLoading(true);
    try {
      const response = await memberFeeClient.searchMemberFees({
        page: 1,
        pageSize: 1000,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: { memberTypeId: id },
      });
      setFees(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching Member Fees:', error);
    } finally {
      setIsFeesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  const handleMemberTypeSubmit = async (data: any) => {
    try {
      await updateMemberType.mutateAsync({ id: id ?? '', data });
      navigate('/admin/membertypes');
    } catch (error) {
      console.error('Error updating Member Type:', error);
    }
  };

  const closeDrawer = () => {
    setDrawerView(null);
    setSelectedFee(null);
  };

  const handleSaveFee = async (data: MemberFeeDTO) => {
    setIsDrawerSaving(true);
    try {
      if (data.id) {
        await memberFeeClient.updateMemberFee(data.id, data);
      } else {
        await memberFeeClient.createMemberFee(data);
      }
      closeDrawer();
      await fetchFees();
    } catch (error) {
      console.error('Error saving Member Fee:', error);
    } finally {
      setIsDrawerSaving(false);
    }
  };

  const handleDeleteFee = async (row: MemberFeeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed || !row.id) {
      return;
    }

    try {
      await memberFeeClient.deleteMemberFee(row.id);
      await fetchFees();
    } catch (error) {
      console.error('Error deleting Member Fee:', error);
    }
  };

  const drawerTitles: Record<Exclude<DrawerView, null>, string> = {
    'add-fee': 'Add Fee',
    'edit-fee': 'Edit Fee',
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/membertypes"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.MemberType.Edit"
    >
      {entityData ? (
        <>
          <MemberTypeForm
            initialValues={entityData}
            onSubmit={handleMemberTypeSubmit}
            onCancel={() => navigate('/admin/membertypes')}
          />

          <FormSection
            id="fees"
            title="Fees"
            subtitle="Recurring fees for this member type"
            headerActions={
              <AddNewButton
                label="Add Fee"
                onClick={() => {
                  setSelectedFee(null);
                  setDrawerView('add-fee');
                }}
              />
            }
          >
            <MemberFeeTable
              data={fees}
              isLoading={isFeesLoading}
              handleEdit={(row) => {
                setSelectedFee(row);
                setDrawerView('edit-fee');
              }}
              handleDelete={handleDeleteFee}
            />
          </FormSection>
        </>
      ) : (
        <p>Loading...</p>
      )}

      <Drawer
        isOpen={drawerView !== null}
        onClose={closeDrawer}
        title={drawerView ? drawerTitles[drawerView] : ''}
      >
        {(drawerView === 'add-fee' || drawerView === 'edit-fee') && id && (
          <MemberFeeForm
            initialValues={selectedFee || ({} as MemberFeeDTO)}
            memberTypeId={id}
            onSubmit={handleSaveFee}
            onCancel={closeDrawer}
            isLoading={isDrawerSaving}
          />
        )}
      </Drawer>
    </AdminLayout>
  );
};

export default MemberTypeEdit;
